import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CancelOutlined as CancelOutlinedIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  api,
  type PodcastVideoRecord,
  type TopViewingSitePlan,
} from '../../services/api';
import {
  MAX_PROJECT_TAGS,
  PodcastVideoPlayerDialog,
  formatCreatedDate,
  getSourceMeta,
  sourceFromUrl,
  canPlayUrl,
  AddVideoPreview,
  type PodcastSource,
} from '../../components/podcast/PodcastVideosTab';

// ---------------------------------------------------------------------------
// My Podcasts dialog
//
// Lists the current user's podcast video submissions in a compact
// card grid (3 per row on desktop, 2 on tablet, 1 on mobile). Each
// card surfaces edit + delete affordances. Editing opens a nested
// dialog where the user can change the tagged projects only — the
// video URL and source are immutable post-create.
//
// Search + project filter mirror the Podcast Videos tab so the UX
// stays consistent for users moving between the two surfaces.
// ---------------------------------------------------------------------------

interface MyPodcastsDialogProps {
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  isAccelerator?: boolean;
  isVideoUploader?: boolean;
}

// Hard cap mirroring the public dialog's grid card size so the masonry
// preview never balloons on wide screens.
const CARD_THUMB_HEIGHT = 140;
// 12 rows per page = 4 rows × 3 cols on desktop. Matches the Manage
// Podcast Videos dialog so both surfaces feel consistent.
const PAGE_SIZE = 12;

const MyPodcastsDialog: React.FC<MyPodcastsDialogProps> = ({
  open,
  onClose,
  isAdmin = false,
  isAccelerator = false,
  isVideoUploader = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- Listing state -------------------------------------------------------
  const [videos, setVideos] = useState<PodcastVideoRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterProjectIds, setFilterProjectIds] = useState<string[]>([]);
  // Surface-partition filter (podcast vs training). Defaults to
  // 'all' so submitters keep seeing every row they've ever
  // submitted; selecting a specific type lets them focus on one
  // surface at a time.
  const [typeFilter, setTypeFilter] = useState<'all' | 'podcast' | 'training'>('all');
  // Currently playing video — `null` collapses the player dialog. We
  // pass the raw `PodcastVideoRecord` to the shared player dialog so
  // it can derive the title/source/aspect itself.
  const [activeRecord, setActiveRecord] = useState<PodcastVideoRecord | null>(null);

  // Review status filter. Defaults to 'verified' so users land on
  // their published submissions. The Pending tab holds rows still
  // awaiting admin review; the Rejected tab surfaces the admin's
  // rejection note so the submitter knows what to fix.
  type StatusTab = 'verified' | 'pending' | 'rejected';
  const [statusTab, setStatusTab] = useState<StatusTab>('verified');

  // Page index per tab. Reset whenever the search / project filter /
  // tab changes so the user never lands on an out-of-range page.
  const [pageByTab, setPageByTab] = useState<Record<StatusTab, number>>({
    verified: 1,
    pending: 1,
    rejected: 1,
  });

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await api.listMyPodcastVideos();
      setVideos(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load podcast videos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh whenever the dialog opens — keeps the list in sync with
  // anything submitted in the Podcast Videos tab between sessions.
  useEffect(() => {
    if (!open) return;
    loadVideos();
  }, [open, loadVideos]);

  // --- Project options (for filter + edit autocomplete) --------------------
  const [projectOptions, setProjectOptions] = useState<TopViewingSitePlan[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState<boolean>(false);
  const projectsLoadStartedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!open) return;
    if (projectsLoadStartedRef.current) return;
    projectsLoadStartedRef.current = true;
    setProjectOptionsLoading(true);
    api
      .getTopViewingSitePlans()
      .then((rows) => setProjectOptions(rows))
      .catch(() => {
        // Best-effort — filter dropdown will just be empty.
      })
      .finally(() => setProjectOptionsLoading(false));
  }, [open]);

  // --- Add Video Dialog State ----------------------------------------------
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlTouched, setUrlTouched] = useState<boolean>(false);
  const [videoTypeInput, setVideoTypeInput] = useState<'podcast' | 'training'>('podcast');
  const trimmedUrl = urlInput.trim();
  const urlValid = trimmedUrl.length > 0 && canPlayUrl(trimmedUrl);
  const previewSource = urlValid ? sourceFromUrl(trimmedUrl) : null;
  const urlShowError = urlTouched && trimmedUrl.length > 0 && !urlValid;

  const [previewMeta, setPreviewMeta] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const [isProjectLead, setIsProjectLead] = useState<boolean>(false);
  useEffect(() => {
    if (isAdmin || isAccelerator || isVideoUploader) return;
    let cancelled = false;
    (async () => {
      try {
        const { projects } = await api.getMyProjectVerifications();
        if (!cancelled) setIsProjectLead((projects?.length ?? 0) > 0);
      } catch {
        if (!cancelled) setIsProjectLead(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isAccelerator, isVideoUploader, open]);
  const canAddVideo = isAdmin || isAccelerator || isVideoUploader || isProjectLead;

  // Non-admins cannot filter to Training — keep them on podcast/all.
  useEffect(() => {
    if (!isAdmin && typeFilter === 'training') setTypeFilter('all');
  }, [isAdmin, typeFilter]);

  useEffect(() => {
    if (!addOpen || !urlValid) {
      setPreviewMeta(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      api
        .fetchVideoMetadata(trimmedUrl, controller.signal)
        .then((meta) => {
          setPreviewMeta(meta);
          setPreviewLoading(false);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError') return;
          setPreviewMeta(null);
          setPreviewError(
            err instanceof Error ? err.message : 'Failed to load preview',
          );
          setPreviewLoading(false);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [addOpen, urlValid, trimmedUrl]);

  const handleOpenAddDialog = () => {
    setUrlInput('');
    setUrlTouched(false);
    setPreviewMeta(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setSubmitError(null);
    setSubmitting(false);
    setSelectedProjectIds([]);
    setVideoTypeInput('podcast');
    setAddOpen(true);
  };

  const handleCloseAddDialog = () => {
    if (submitting) return;
    setAddOpen(false);
  };

  const handleSubmitAddDialog = async () => {
    if (
      !urlValid ||
      !previewSource ||
      submitting ||
      selectedProjectIds.length === 0 ||
      selectedProjectIds.length > MAX_PROJECT_TAGS
    ) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.createPodcastVideo({
        videoUrl: trimmedUrl,
        videoSource: getSourceMeta(previewSource).label,
        thumbnailUrl: previewMeta?.image ?? null,
        title: previewMeta?.title ?? null,
        projectIds: selectedProjectIds,
        videoType: videoTypeInput,
      });
      setAddOpen(false);
      loadVideos();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to add video',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --- Edit dialog state ---------------------------------------------------
  const [editing, setEditing] = useState<PodcastVideoRecord | null>(null);

  // --- Filtering -----------------------------------------------------------
  // First narrow by search + project filter, then count + partition by
  // verification status so the tab labels show the matching counts and
  // each tab renders only the rows it owns.
  const matchedVideos = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return videos.filter((v) => {
      if (typeFilter !== 'all') {
        const rowType = v.videoType ?? 'podcast';
        if (rowType !== typeFilter) return false;
      }
      if (filterProjectIds.length > 0) {
        const tagged = (v.projects ?? []).map((p) => p.id);
        if (!filterProjectIds.some((pid) => tagged.includes(pid))) return false;
      }
      if (needle) {
        const haystack = [
          v.title ?? '',
          v.videoUrl,
          v.videoSource,
          ...(v.projects ?? []).map((p) => p.name),
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [videos, searchQuery, filterProjectIds, typeFilter]);

  // Partition the matched set by status once so we can read each
  // tab's count + render its rows without re-filtering.
  const verifiedRows = useMemo(
    () => matchedVideos.filter((v) => v.status === 'verified'),
    [matchedVideos],
  );
  const pendingRows = useMemo(
    () => matchedVideos.filter((v) => v.status === 'pending'),
    [matchedVideos],
  );
  const rejectedRows = useMemo(
    () => matchedVideos.filter((v) => v.status === 'rejected'),
    [matchedVideos],
  );

  const filteredVideos =
    statusTab === 'verified'
      ? verifiedRows
      : statusTab === 'pending'
        ? pendingRows
        : rejectedRows;

  // Reset page indices whenever the inputs that change `filteredVideos`
  // size change. We don't watch `filteredVideos` itself because edits
  // (e.g. tag changes) shouldn't bounce users back to page 1.
  useEffect(() => {
    setPageByTab({ verified: 1, pending: 1, rejected: 1 });
  }, [searchQuery, filterProjectIds, typeFilter]);

  const currentPage = pageByTab[statusTab];
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedVideos = useMemo(
    () =>
      filteredVideos.slice(
        (safePage - 1) * PAGE_SIZE,
        safePage * PAGE_SIZE,
      ),
    [filteredVideos, safePage],
  );

  // --- Delete --------------------------------------------------------------
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const handleDelete = useCallback(
    async (record: PodcastVideoRecord) => {
      const ok = window.confirm(
        `Delete "${record.title || record.videoUrl}"? This cannot be undone.`,
      );
      if (!ok) return;
      setDeletingId(record.id);
      try {
        await api.deletePodcastVideo(record.id);
        setVideos((prev) => prev.filter((v) => v.id !== record.id));
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Failed to delete');
      } finally {
        setDeletingId(null);
      }
    },
    [],
  );

  // --- Render --------------------------------------------------------------
  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            fontWeight: 700,
          }}
        >
          My Videos
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Review status tabs — defaults to Verified so users land on
            their published submissions. Counts reflect the current
            search + project filter. */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Tabs
            value={statusTab}
            onChange={(_, value: StatusTab) => setStatusTab(value)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ flex: 1 }}
          >
            <Tab
              value="verified"
              label={`Verified${verifiedRows.length > 0 ? ` (${verifiedRows.length})` : ''}`}
            />
            <Tab
              value="pending"
              label={`Pending${pendingRows.length > 0 ? ` (${pendingRows.length})` : ''}`}
            />
            <Tab
              value="rejected"
              label={`Rejected${rejectedRows.length > 0 ? ` (${rejectedRows.length})` : ''}`}
            />
          </Tabs>
          {canAddVideo && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                mb: { xs: 1, sm: 0 },
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'var(--color-accent-strong, var(--color-accent))',
                },
              }}
            >
              Add Video
            </Button>
          )}
        </Box>

        <DialogContent dividers sx={{ bgcolor: 'var(--color-bg)' }}>
          {/* Search + project filter row — mirrors the Podcast Videos tab. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search by title, URL, source, or project"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              // 50/50 split with the project filter on >=sm screens;
              // stacks full-width on mobile via the Stack's
              // responsive `direction`.
              sx={{
                width: { xs: '100%', sm: '50%' },
                bgcolor: 'var(--color-surface)',
              }}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              loading={projectOptionsLoading}
              options={projectOptions}
              sx={{
                width: { xs: '100%', sm: '50%' },
                bgcolor: 'var(--color-surface)',
              }}
              value={filterProjectIds
                .map((id) => projectOptions.find((p) => p.projectId === id))
                .filter((p): p is TopViewingSitePlan => Boolean(p))}
              onChange={(_, value) =>
                setFilterProjectIds(value.map((p) => p.projectId))
              }
              getOptionLabel={(opt) => opt.projectName || opt.projectId}
              isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    filterProjectIds.length === 0 ? 'Filter by project(s)' : ''
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, idx) => (
                  <Chip
                    {...getTagProps({ index: idx })}
                    key={opt.projectId}
                    label={opt.projectName || opt.projectId}
                    size="small"
                  />
                ))
              }
            />
          </Stack>

          {/* Surface-partition filter — applies across every status
              tab so submitters can focus on either the public Podcast
              Videos surface or the per-project Training Videos
              surface. Defaults to all. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              Type
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={typeFilter}
              onChange={(_, value: 'all' | 'podcast' | 'training' | null) => {
                if (value) setTypeFilter(value);
              }}
              aria-label="Filter videos by type"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.4,
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="podcast">Podcast</ToggleButton>
              {isAdmin && <ToggleButton value="training">Training</ToggleButton>}
            </ToggleButtonGroup>
          </Stack>

          {/* Body states */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : loadError ? (
            <Typography variant="body2" sx={{ color: 'error.main', py: 4, textAlign: 'center' }}>
              {loadError}
            </Typography>
          ) : filteredVideos.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', py: 6, textAlign: 'center' }}
            >
              {videos.length === 0
                ? "You haven't added any podcast videos yet."
                : matchedVideos.length === 0
                  ? 'No podcast videos match the current filters.'
                  : statusTab === 'verified'
                    ? 'No verified podcast videos match the current filters.'
                    : statusTab === 'pending'
                      ? 'No pending podcast videos match the current filters.'
                      : 'No rejected podcast videos match the current filters.'}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {pagedVideos.map((record) => (
                  <MyPodcastCard
                    key={record.id}
                    record={record}
                    isDeleting={deletingId === record.id}
                    onPlay={() => setActiveRecord(record)}
                    onEdit={() => setEditing(record)}
                    onDelete={() => handleDelete(record)}
                  />
                ))}
              </Box>
              {/* Pagination — only visible when more than one page,
                  matching the Manage Podcast Videos dialog. */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2.5,
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={safePage}
                    onChange={(_, value) =>
                      setPageByTab((prev) => ({ ...prev, [statusTab]: value }))
                    }
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      {editing && (
        <EditPodcastDialog
          open={Boolean(editing)}
          record={editing}
          projectOptions={projectOptions}
          projectOptionsLoading={projectOptionsLoading}
          onClose={() => setEditing(null)}
          onSaved={(updatedProjects) => {
            // Patch in place so the grid reflects the new tags without
            // a full reload round-trip.
            setVideos((prev) =>
              prev.map((v) =>
                v.id === editing.id ? { ...v, projects: updatedProjects } : v,
              ),
            );
            setEditing(null);
          }}
        />
      )}

      {/* Player dialog — reuses the same embed + aspect logic as the
          public Podcast Videos tab so the video, title, and
          "Submitted by … on …" line all render consistently. */}
      <PodcastVideoPlayerDialog
        record={activeRecord}
        onClose={() => setActiveRecord(null)}
      />

      {/* Add Video Dialog */}
      <Dialog
        open={addOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            pb: 1,
          }}
        >
          Add Video
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', mb: 1 }}
          >
            Supported media platforms:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {(
              ['youtube', 'facebook', 'instagram', 'tiktok', 'rednote'] as const
            ).map((s) => {
              const meta = getSourceMeta(s);
              return (
                <Box
                  key={s}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: meta.color,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {meta.icon}
                  {meta.label}
                </Box>
              );
            })}
          </Stack>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="https://www.youtube.com/watch?v=..."
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              if (!urlTouched) setUrlTouched(true);
            }}
            onBlur={() => setUrlTouched(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && urlValid) {
                e.preventDefault();
                handleSubmitAddDialog();
              }
            }}
            error={urlShowError}
            helperText={urlShowError ? 'Please input a valid video url' : ' '}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'var(--color-surface-elevated)',
                borderRadius: 2,
              },
              '& .MuiInputBase-input': {
                color: 'var(--color-text)',
                fontWeight: 500,
              },
            }}
          />

          {urlValid && previewSource && (
            <AddVideoPreview
              url={trimmedUrl}
              source={previewSource}
              meta={previewMeta}
              loading={previewLoading}
              error={previewError}
            />
          )}

          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-muted)',
              mt: 2,
              mb: 0.5,
              fontWeight: 600,
            }}
          >
            Video Type
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={videoTypeInput}
            onChange={(e) => setVideoTypeInput(e.target.value as 'podcast' | 'training')}
            SelectProps={{ native: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'var(--color-surface-elevated)',
                borderRadius: 2,
              },
              '& select': {
                color: 'var(--color-text)',
                fontWeight: 500,
                py: 1,
              },
            }}
          >
            <option value="podcast" style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}>Podcast</option>
            <option value="training" style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}>Training</option>
          </TextField>

          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-muted)',
              mt: 2.5,
              mb: 0.5,
              fontWeight: 600,
            }}
          >
            Tag to project(s)
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'var(--color-text-muted)',
              mb: 1,
            }}
          >
            Select up to {MAX_PROJECT_TAGS} projects.
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            loading={projectOptionsLoading}
            options={projectOptions}
            value={selectedProjectIds
              .map((id) => projectOptions.find((p) => p.projectId === id))
              .filter((p): p is TopViewingSitePlan => Boolean(p))}
            onChange={(_, value) => {
              const next = value
                .slice(0, MAX_PROJECT_TAGS)
                .map((p) => p.projectId);
              setSelectedProjectIds(next);
            }}
            getOptionDisabled={(opt) =>
              selectedProjectIds.length >= MAX_PROJECT_TAGS &&
              !selectedProjectIds.includes(opt.projectId)
            }
            getOptionLabel={(opt) => opt.projectName || opt.projectId}
            isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                placeholder={
                  selectedProjectIds.length === 0
                    ? 'Select one or more projects'
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'var(--color-surface)',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={opt.projectId}
                  label={opt.projectName || opt.projectId}
                  size="small"
                />
              ))
            }
          />

          {submitError && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.5,
                color: 'error.main',
              }}
            >
              {submitError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseAddDialog}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAddDialog}
            variant="contained"
            disabled={
              !urlValid ||
              !previewSource ||
              submitting ||
              selectedProjectIds.length === 0 ||
              selectedProjectIds.length > MAX_PROJECT_TAGS
            }
            startIcon={
              submitting ? (
                <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
              ) : null
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'var(--color-accent-strong, var(--color-accent))',
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyPodcastsDialog;

// ---------------------------------------------------------------------------
// MyPodcastCard
// ---------------------------------------------------------------------------

interface MyPodcastCardProps {
  record: PodcastVideoRecord;
  isDeleting: boolean;
  onPlay: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const MyPodcastCard: React.FC<MyPodcastCardProps> = ({
  record,
  isDeleting,
  onPlay,
  onEdit,
  onDelete,
}) => {
  const source: PodcastSource = sourceFromUrl(record.videoUrl) ?? 'youtube';
  const sourceMeta = getSourceMeta(source);
  const title = record.title || record.videoUrl;
  const projects = record.projects ?? [];
  // Displayed under the title.
  //  - Verified rows: "Verified by {admin name} on {reviewedAt}"
  //  - Rejected rows: "Rejected by {admin name} on {reviewedAt}"
  //                   + the reviewNote rendered separately below.
  //  - Pending rows : "Submitted by {submitter name} on {createdAt}"
  // Each branch falls back to the CEA when the joined User row is
  // missing, and silently drops " on {date}" when the timestamp is
  // null so we never render a dangling " on ".
  const attributionLine = (() => {
    if (record.status === 'verified' || record.status === 'rejected') {
      const reviewerName =
        record.reviewedByName || record.reviewedBy || 'an admin';
      const reviewedDate = formatCreatedDate(record.reviewedAt ?? null);
      const verb = record.status === 'verified' ? 'Verified' : 'Rejected';
      return reviewedDate
        ? `${verb} by ${reviewerName} on ${reviewedDate}`
        : `${verb} by ${reviewerName}`;
    }
    const submitterName = record.createdByName || record.createdBy;
    const submittedDate = formatCreatedDate(record.createdAt);
    return submittedDate
      ? `Submitted by ${submitterName} on ${submittedDate}`
      : `Submitted by ${submitterName}`;
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid var(--color-border-strong)',
        bgcolor: 'var(--color-surface)',
        overflow: 'hidden',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: 'var(--color-accent)',
          boxShadow: 'inset 0 0 0 1px var(--color-accent)',
        },
      }}
    >
      {/* Thumbnail — clicking anywhere on this surface opens the
          shared video player dialog. Uses a real <button> for
          accessible keyboard activation. */}
      <Box
        component="button"
        type="button"
        onClick={onPlay}
        aria-label={`Play ${title}`}
        sx={{
          position: 'relative',
          width: '100%',
          height: CARD_THUMB_HEIGHT,
          bgcolor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          // The Play-circle overlay below relies on hover state on
          // this button to fade in.
          '&:hover .play-overlay': { opacity: 1 },
          '&:focus-visible': {
            outline: '2px solid var(--color-accent)',
            outlineOffset: -2,
          },
        }}
      >
        {record.thumbnailUrl ? (
          <Box
            component="img"
            src={record.thumbnailUrl}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <PlayCircleOutlineIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.6)' }} />
        )}
        {/* Hover-revealed play affordance so the card visibly
            advertises that it's clickable. */}
        <Box
          className="play-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.25)',
            opacity: 0,
            transition: 'opacity 120ms ease',
            pointerEvents: 'none',
          }}
        >
          <PlayCircleOutlineIcon
            sx={{ fontSize: 48, color: 'rgba(255,255,255,0.95)' }}
          />
        </Box>
        {/* Source badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: sourceMeta.color,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {sourceMeta.icon}
          {sourceMeta.label}
        </Box>
        {/* Surface-partition badge — sits below the source badge so
            submitters can tell at a glance whether the row belongs
            to the public Podcast Videos feed or the per-project
            Training Videos dialog. */}
        <Box
          sx={{
            position: 'absolute',
            top: 36,
            left: 8,
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            bgcolor:
              (record.videoType ?? 'podcast') === 'training'
                ? 'rgba(16, 185, 129, 0.9)'
                : 'rgba(99, 102, 241, 0.9)',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {(record.videoType ?? 'podcast') === 'training' ? 'Training' : 'Podcast'}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', flex: 1, gap: 0.75 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.3,
          }}
          title={title}
        >
          {title}
        </Typography>
        {/* Submitted-by line — surfaces the submitter and the
            submission date so admins reviewing the list have the
            context they need without opening the player. */}
        <Typography
          variant="caption"
          sx={{ color: 'var(--color-text-muted)', display: 'block' }}
        >
          {attributionLine}
        </Typography>
        {/* Inline rejection reason — clipped to a single line on the
            card. The submitter can open the edit dialog to see the
            full note if it overflows. */}
        {record.status === 'rejected' && record.reviewNote && (
          <Typography
            variant="caption"
            title={record.reviewNote}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              p: 0.75,
              borderRadius: 1,
              bgcolor: 'rgba(211, 47, 47, 0.06)',
              color: '#c62828',
              fontWeight: 500,
            }}
          >
            <strong>Reason:</strong> {record.reviewNote}
          </Typography>
        )}
        {projects.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {projects.map((p) => (
              <Chip key={p.id} size="small" label={p.name} sx={{ height: 20 }} />
            ))}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          <Tooltip title="Edit project tags" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={isDeleting}
                sx={{ color: 'var(--color-accent)' }}
                aria-label="Edit podcast"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete podcast" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={isDeleting}
                sx={{ color: 'error.main' }}
                aria-label="Delete podcast"
              >
                {isDeleting ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: 'error.main' }} />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// EditPodcastDialog — read-only URL/preview/source, editable tags
// ---------------------------------------------------------------------------

interface EditPodcastDialogProps {
  open: boolean;
  record: PodcastVideoRecord;
  projectOptions: TopViewingSitePlan[];
  projectOptionsLoading: boolean;
  onClose: () => void;
  onSaved: (updatedProjects: { id: string; name: string }[]) => void;
}

const EditPodcastDialog: React.FC<EditPodcastDialogProps> = ({
  open,
  record,
  projectOptions,
  projectOptionsLoading,
  onClose,
  onSaved,
}) => {
  const source: PodcastSource = sourceFromUrl(record.videoUrl) ?? 'youtube';
  const sourceMeta = getSourceMeta(source);
  const title = record.title || record.videoUrl;

  // Seed the edit autocomplete with the currently tagged projects.
  const initialIds = useMemo(
    () => (record.projects ?? []).map((p) => p.id),
    [record.projects],
  );
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(initialIds);

  useEffect(() => {
    setSelectedProjectIds(initialIds);
  }, [initialIds]);

  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    if (saving) return;
    if (
      selectedProjectIds.length === 0 ||
      selectedProjectIds.length > MAX_PROJECT_TAGS
    ) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await api.updatePodcastVideoProjects(record.id, selectedProjectIds);
      // Build the new tags payload from the autocomplete options so
      // the parent can patch its row without an extra round-trip.
      const updatedProjects = selectedProjectIds.map((id) => {
        const opt = projectOptions.find((p) => p.projectId === id);
        return { id, name: opt?.projectName || id };
      });
      onSaved(updatedProjects);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700,
        }}
      >
        Edit Podcast
        <IconButton onClick={onClose} disabled={saving} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* URL — read-only hyperlink. Opens the source platform in a
            new tab; the URL itself can never be edited from this
            dialog. */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Video URL
        </Typography>
        <Typography
          component="a"
          href={record.videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            display: 'block',
            mb: 2,
            color: 'var(--color-accent)',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            '&:hover': { textDecoration: 'none' },
          }}
        >
          {record.videoUrl}
        </Typography>

        {/* Preview thumbnail */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxHeight: 260,
            aspectRatio: '16 / 9',
            bgcolor: '#000',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {record.thumbnailUrl ? (
            <Box
              component="img"
              src={record.thumbnailUrl}
              alt={title}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <PlayCircleOutlineIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.6)' }} />
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: sourceMeta.color,
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {sourceMeta.icon}
            {sourceMeta.label}
          </Box>
        </Box>

        {/* Type (source label) */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Type
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {sourceMeta.label}
        </Typography>

        {/* Review status — read-only. Mirrors the row's `status`
            column; only an admin can change it via the Manage
            Podcast Videos dialog. Three colour states match the
            tab grammar in My Podcasts. */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Status
        </Typography>
        {(() => {
          // Read-only chip palette per status. Keeps the JSX below
          // declarative without three nested ternaries.
          const meta =
            record.status === 'verified'
              ? {
                  label: 'Verified by admin',
                  icon: <VerifiedOutlinedIcon sx={{ fontSize: 16 }} />,
                  bg: 'rgba(46, 125, 50, 0.12)',
                  fg: '#2e7d32',
                }
              : record.status === 'rejected'
                ? {
                    label: 'Rejected',
                    icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
                    bg: 'rgba(211, 47, 47, 0.12)',
                    fg: '#c62828',
                  }
                : {
                    label: 'Pending review',
                    icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
                    bg: 'rgba(237, 108, 2, 0.12)',
                    fg: '#ed6c02',
                  };
          return (
            <Chip
              size="small"
              icon={meta.icon}
              label={meta.label}
              sx={{
                mb: record.status === 'rejected' && record.reviewNote ? 1 : 2,
                bgcolor: meta.bg,
                color: meta.fg,
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          );
        })()}

        {/* Rejection reason — only on rejected rows. Surfaced
            verbatim so the submitter knows what to fix before
            re-submitting (rejection is terminal). */}
        {record.status === 'rejected' && record.reviewNote && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 2,
              p: 1,
              borderRadius: 1,
              bgcolor: 'rgba(211, 47, 47, 0.06)',
              color: 'var(--color-text-strong)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <strong>Reason:</strong> {record.reviewNote}
          </Typography>
        )}

        {/* Tag to project(s) */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Tag to project(s)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            mb: 1,
          }}
        >
          Select up to {MAX_PROJECT_TAGS} projects.
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          loading={projectOptionsLoading}
          options={projectOptions}
          value={selectedProjectIds
            .map((id) => projectOptions.find((p) => p.projectId === id))
            .filter((p): p is TopViewingSitePlan => Boolean(p))}
          onChange={(_, value) => {
            const next = value
              .slice(0, MAX_PROJECT_TAGS)
              .map((p) => p.projectId);
            setSelectedProjectIds(next);
          }}
          getOptionDisabled={(opt) =>
            selectedProjectIds.length >= MAX_PROJECT_TAGS &&
            !selectedProjectIds.includes(opt.projectId)
          }
          getOptionLabel={(opt) => opt.projectName || opt.projectId}
          isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="off"
              inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              placeholder={
                selectedProjectIds.length === 0 ? 'Select one or more projects' : ''
              }
              sx={{ bgcolor: 'var(--color-surface)' }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, idx) => (
              <Chip
                {...getTagProps({ index: idx })}
                key={opt.projectId}
                label={opt.projectName || opt.projectId}
                size="small"
              />
            ))
          }
        />

        {saveError && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'error.main' }}>
            {saveError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving ||
            selectedProjectIds.length === 0 ||
            selectedProjectIds.length > MAX_PROJECT_TAGS
          }
          startIcon={
            saving ? (
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            ) : null
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: 'var(--color-accent)',
            '&:hover': {
              bgcolor: 'var(--color-accent-strong, var(--color-accent))',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
